import React, { useEffect, useMemo, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Calendar, Clock, MapPin, Phone, Mail, Instagram, Star, Send, ArrowRight, MessageCircle, ShoppingBag, Plus, Minus, X, Trash2, User as UserIcon, LogIn, Sparkles, Award, Heart, Shield, Crown, Gem, Leaf, Users, ThumbsUp, Smile, Scissors, Package as PackageIcon, Gift } from "lucide-react";
import SEO from "@/components/SEO";
import { buildKeywords, buildDescription, slugify } from "@/lib/seoKeywords";

// Map operator-selected icon names (stored in CMS) to the actual lucide-react
// component. Keep this list in sync with FEATURE_ICONS in WebsiteCMS.jsx.
const FEATURE_ICON_MAP = {
  Sparkles, Award, Heart, Shield, Star, Crown, Gem, Leaf, Clock, Users, ThumbsUp, Smile,
};

const resolveImg = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return API_BASE.replace(/\/api$/, "") + u;
};

export default function PublicSalonSite() {
  const { id } = useParams();
  const salonId = id || "default";
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [lang, setLang] = useState("ar");
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [cart, setCart] = useState([]);
  const [bookingForm, setBookingForm] = useState({ customer_name: "", customer_phone: "", customer_email: "", date: "", time: "", notes: "", coupon_code: "", create_account: false });
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewForm, setReviewForm] = useState({ customer_name: "", customer_phone: "", rating: 5, comment: "", service_id: "" });
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [authedCustomer, setAuthedCustomer] = useState(null);
  // Public site v4 — single tabbed catalog (Services / Packages / Products).
  const [catalogTab, setCatalogTab] = useState("services");
  // Catalog search — searches across services + packages + products by name.
  const [catalogQ, setCatalogQ] = useState("");
  // Per-tab "show all" toggle — default OFF so we render only the first 4.
  const [showAllByTab, setShowAllByTab] = useState({ services: false, packages: false, products: false });

  const isRTL = lang === "ar";

  useEffect(() => {
    api.get(`/public/site/${salonId}`).then((r) => setData(r.data)).catch(() => toast.error("Failed to load"));
    // Check if customer is logged in
    api.get("/customer/me").then((r) => setAuthedCustomer(r.data)).catch(() => setAuthedCustomer(null));
  }, [salonId]);

  useEffect(() => {
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    document.documentElement.lang = lang;
  }, [lang]);

  // Fetch slot availability whenever date changes
  useEffect(() => {
    if (!bookingForm.date) { setSlots([]); return; }
    setSlotsLoading(true);
    api.get(`/public/slots?date=${bookingForm.date}`)
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [bookingForm.date]);

  const addToCart = (kind, item) => {
    setCart((prev) => {
      const exist = prev.find((p) => p.kind === kind && p.ref_id === (item.service_id || item.package_id));
      if (exist) {
        return prev.map((p) => (p === exist ? { ...p, qty: p.qty + 1 } : p));
      }
      return [...prev, {
        kind,
        ref_id: item.service_id || item.package_id,
        name_en: item.name_en,
        name_ar: item.name_ar,
        price: item.price,
        qty: 1,
      }];
    });
    toast.success(isRTL ? "تمت الإضافة" : "Added");
  };

  const updateQty = (idx, delta) => {
    setCart((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], qty: Math.max(1, next[idx].qty + delta) };
      return next;
    });
  };
  const removeFromCart = (idx) => setCart((prev) => prev.filter((_, i) => i !== idx));
  const clearCart = () => setCart([]);

  const cartTotal = cart.reduce((s, c) => s + c.price * c.qty, 0);
  const cartCount = cart.reduce((s, c) => s + c.qty, 0);

  const openBookingWithCart = () => {
    if (cart.length === 0) {
      toast.error(isRTL ? "اختر خدمة أو باقة على الأقل" : "Select at least one service or package");
      return;
    }
    setShowBookingModal(true);
  };

  const submitBooking = async () => {
    if (cart.length === 0) {
      toast.error(isRTL ? "السلة فارغة" : "Cart is empty");
      return;
    }
    if (!bookingForm.customer_name || !bookingForm.customer_phone || !bookingForm.date || !bookingForm.time) {
      toast.error(isRTL ? "أكمل البيانات المطلوبة" : "Complete required fields");
      return;
    }
    try {
      const items = cart.map((c) => ({ kind: c.kind, ref_id: c.ref_id, qty: c.qty }));
      await api.post("/public/booking", { ...bookingForm, salon_id: salonId, items });
      toast.success(isRTL ? "تم الحجز! سنتواصل معك قريباً" : "Booking confirmed!");
      setShowBookingModal(false);
      clearCart();
      setBookingForm({ customer_name: "", customer_phone: "", customer_email: "", date: "", time: "", notes: "", coupon_code: "", create_account: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || (isRTL ? "خطأ في الحجز" : "Booking failed"));
    }
  };

  const submitReview = async () => {
    if (!reviewForm.customer_name || !reviewForm.comment) {
      toast.error(isRTL ? "املأ الحقول" : "Fill the form");
      return;
    }
    try {
      await api.post("/public/review", reviewForm);
      toast.success(isRTL ? "شكراً على تقييمك!" : "Thanks for your review!");
      setShowReviewModal(false);
      setReviewForm({ customer_name: "", customer_phone: "", rating: 5, comment: "", service_id: "" });
    } catch {
      toast.error("Error");
    }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center bg-[#FBF5F1]">Loading...</div>;
  const { cms, services, packages, products = [], reviews } = data;

  // ─────────── Catalog search + paging helpers ───────────
  // The customer types a single query → we match against EN/AR names and
  // descriptions across all three catalogs. Empty query falls back to the
  // current `catalogTab` view with a hard 4-item cap unless the user clicks
  // "Browse all".
  const normalize = (s) => (s || "").toString().toLowerCase().trim();
  const cq = normalize(catalogQ);
  const matches = (item) => {
    if (!cq) return true;
    return [item.name_en, item.name_ar, item.description_en, item.description_ar, item.category]
      .map(normalize).some((v) => v && v.includes(cq));
  };
  const matchedServices = (services || []).filter(matches);
  const matchedPackages = (packages || []).filter(matches);
  const matchedProducts = (products || []).filter(matches);
  const searchActive = cq.length > 0;
  const PREVIEW_LIMIT = 4;
  const visibleServices = searchActive ? matchedServices : (showAllByTab.services ? matchedServices : matchedServices.slice(0, PREVIEW_LIMIT));
  const visiblePackages = searchActive ? matchedPackages : (showAllByTab.packages ? matchedPackages : matchedPackages.slice(0, PREVIEW_LIMIT));
  const visibleProducts = searchActive ? matchedProducts : (showAllByTab.products ? matchedProducts : matchedProducts.slice(0, PREVIEW_LIMIT));
  const totalMatches = matchedServices.length + matchedPackages.length + matchedProducts.length;

  // Featured = items the operator marked is_featured=true across all three
  // catalogs. Services come first, then packages, then products so the most
  // bookable surface area shows up at the top of the featured band.
  const featuredItems = [
    ...services.filter(s => s.is_featured),
    ...packages.filter(p => p.is_featured),
    ...products.filter(p => p.is_featured),
  ];

  const primary = cms.primary_color || "#7A1F3D";
  const accent = cms.accent_color || "#9B2950";

  // ───────────────────────── SEO data builder ─────────────────────────
  const salonName = (isRTL ? cms.name_ar : cms.name_en) || (isRTL ? cms.name_en : cms.name_ar) || "iSaloon";
  const cityFromAddr = (() => {
    // Try to extract city: take the segment after a comma, fallback to default Doha for QA
    const addr = (isRTL ? cms.address_ar : cms.address_en) || cms.address_en || cms.address_ar || "";
    const parts = addr.split(/[,،]/).map((s) => s.trim()).filter(Boolean);
    if (parts.length >= 2) return parts[parts.length - 1];
    return isRTL ? "الدوحة" : "Doha";
  })();

  const seoSlug = slugify(cms.slug || cms.name_en || cms.name_ar || salonId);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const canonical = `${origin}/salon/${salonId}`;
  const altAR = `${origin}/salon/${salonId}?lang=ar`;
  const altEN = `${origin}/salon/${salonId}?lang=en`;

  const kwAR = buildKeywords({ lang: "ar", salonName: cms.name_ar || salonName, city: cityFromAddr, services, customKeywords: cms.seo_keywords_ar || cms.seo_keywords || "" });
  const kwEN = buildKeywords({ lang: "en", salonName: cms.name_en || salonName, city: cityFromAddr, services, customKeywords: cms.seo_keywords_en || cms.seo_keywords || "" });
  const kw = isRTL ? kwAR : kwEN;

  const seoTitle = (() => {
    const t = isRTL
      ? `${salonName} | حجز صالون اونلاين${cityFromAddr ? ` في ${cityFromAddr}` : ""} — iSaloon`
      : `${salonName} | Online Beauty Salon Booking${cityFromAddr ? ` in ${cityFromAddr}` : ""} — iSaloon`;
    return t.slice(0, 70);
  })();

  const seoDescription = cms.seo_description || buildDescription({
    lang: isRTL ? "ar" : "en",
    salonName,
    city: cityFromAddr,
    services,
    topKeywords: kw.top,
  });

  const ogImage = cms.cover_url ? (cms.cover_url.startsWith("http") ? cms.cover_url : `${origin}${cms.cover_url}`) : (cms.logo_url ? (cms.logo_url.startsWith("http") ? cms.logo_url : `${origin}${cms.logo_url}`) : "");

  // ── JSON-LD: BeautySalon + Services + Offers + Breadcrumb ──
  const businessLd = {
    "@context": "https://schema.org",
    "@type": "BeautySalon",
    name: salonName,
    image: ogImage || undefined,
    "@id": canonical,
    url: canonical,
    telephone: cms.phone || undefined,
    email: cms.email || undefined,
    address: cms.address_en || cms.address_ar
      ? {
          "@type": "PostalAddress",
          streetAddress: cms.address_en || cms.address_ar,
          addressLocality: cityFromAddr,
          addressCountry: "QA",
        }
      : undefined,
    sameAs: [cms.instagram, cms.facebook, cms.tiktok].filter(Boolean),
    priceRange: cms.price_range || "$$",
    openingHours: cms.opening_hours || undefined,
  };

  const servicesLd = (services || []).slice(0, 25).map((s) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    name: isRTL ? s.name_ar : s.name_en,
    description: isRTL ? s.description_ar : s.description_en,
    provider: { "@type": "BeautySalon", name: salonName, url: canonical },
    areaServed: cityFromAddr,
    offers: {
      "@type": "Offer",
      price: s.price,
      priceCurrency: "QAR",
      availability: "https://schema.org/InStock",
    },
  }));

  const packagesLd = (packages || []).slice(0, 15).map((p) => ({
    "@context": "https://schema.org",
    "@type": "Offer",
    name: isRTL ? p.name_ar : p.name_en,
    description: isRTL ? p.description_ar : p.description_en,
    price: p.price,
    priceCurrency: "QAR",
    seller: { "@type": "BeautySalon", name: salonName, url: canonical },
  }));

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: isRTL ? "الرئيسية" : "Home", item: origin || canonical },
      { "@type": "ListItem", position: 2, name: salonName, item: canonical },
    ],
  };

  // Aggregate rating
  const aggLd = (reviews && reviews.length > 0) ? {
    "@context": "https://schema.org",
    "@type": "AggregateRating",
    ratingValue: (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1),
    reviewCount: reviews.length,
    bestRating: 5,
    worstRating: 1,
    itemReviewed: { "@type": "BeautySalon", name: salonName, url: canonical },
  } : null;

  const jsonLd = [businessLd, breadcrumbLd, ...servicesLd, ...packagesLd, aggLd].filter(Boolean);

  return (
    <div className="min-h-screen bg-[#FBF5F1]" style={{ "--p": primary, "--a": accent }} data-testid="public-salon-site">
      <SEO
        title={seoTitle}
        description={seoDescription}
        canonical={canonical}
        keywords={kw.csv}
        image={ogImage}
        type="website"
        lang={isRTL ? "ar" : "en"}
        alternates={{ ar: altAR, en: altEN }}
        siteName={salonName}
        jsonLd={jsonLd}
      />
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {cms.logo_url && <img src={resolveImg(cms.logo_url)} alt="" className="w-10 h-10 rounded object-cover" />}
            <h1 className="text-xl sm:text-2xl font-bold" style={{ color: primary }}>
              {isRTL ? cms.name_ar : cms.name_en}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <a href="#catalog" className="hidden sm:block text-sm text-stone-600 hover:text-[var(--p)] px-3 py-1.5">{isRTL ? "الخدمات" : "Services"}</a>
            {cms.show_about && <a href="#about" className="hidden sm:block text-sm text-stone-600 hover:text-[var(--p)] px-3 py-1.5">{isRTL ? "من نحن" : "About"}</a>}
            {cms.show_gallery && <a href="#gallery" className="hidden md:block text-sm text-stone-600 hover:text-[var(--p)] px-3 py-1.5">{isRTL ? "المعرض" : "Gallery"}</a>}
            {cms.show_contact && <a href="#contact" className="hidden md:block text-sm text-stone-600 hover:text-[var(--p)] px-3 py-1.5">{isRTL ? "تواصل" : "Contact"}</a>}
            <button
              onClick={() => setLang(lang === "ar" ? "en" : "ar")}
              data-testid="public-lang-toggle"
              className="text-sm px-3 py-1.5 border border-stone-300 rounded-md hover:border-[var(--p)] transition"
            >
              {lang === "ar" ? "EN" : "ع"}
            </button>
            {authedCustomer ? (
              <button onClick={() => navigate(`/account/${salonId}`)} data-testid="customer-account-btn" className="text-sm px-3 py-1.5 border border-stone-300 rounded-md hover:border-[var(--p)] transition flex items-center gap-1.5">
                <UserIcon size={14} />
                <span className="hidden sm:inline">{authedCustomer.name?.split(" ")[0] || (isRTL ? "حسابي" : "Account")}</span>
              </button>
            ) : (
              <button onClick={() => navigate(`/account/${salonId}`)} data-testid="customer-login-btn" className="text-sm px-3 py-1.5 border border-stone-300 rounded-md hover:border-[var(--p)] transition flex items-center gap-1.5">
                <LogIn size={14} />
                <span className="hidden sm:inline">{isRTL ? "تسجيل الدخول" : "Login"}</span>
              </button>
            )}
            <button
              onClick={openBookingWithCart}
              data-testid="public-cart-btn"
              className="relative text-white text-sm px-4 py-2 rounded-md hover:opacity-90 transition flex items-center gap-2"
              style={{ background: primary }}
            >
              <ShoppingBag size={16} />
              <span className="hidden sm:inline">{isRTL ? "احجز" : "Book"}</span>
              {cartCount > 0 && <span className="absolute -top-1.5 -end-1.5 w-5 h-5 rounded-full bg-rose-500 text-white text-xs flex items-center justify-center font-bold">{cartCount}</span>}
            </button>
          </div>
        </div>
      </header>

      {/* ═══════════════════ HERO ═══════════════════
          Minimal text-free hero — only the CTA button surfaces over the image
          so the salon's brand photography stays front-and-centre. The full
          title/subtitle/tagline remain in the CMS but are no longer rendered
          on the public site (operator preference, 2026-06-04). */}
      <section
        className="relative overflow-hidden min-h-[60vh] sm:min-h-[70vh] lg:min-h-[80vh] flex items-end justify-center"
        style={{
          backgroundImage: cms.hero_image_url
            ? `linear-gradient(180deg, rgba(28,18,8,0.10) 0%, rgba(28,18,8,0.10) 60%, rgba(28,18,8,0.55) 100%), url(${resolveImg(cms.hero_image_url)})`
            : `linear-gradient(135deg, ${primary}, ${accent})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Decorative gradient blobs */}
        <div className="absolute -top-32 -end-32 w-80 h-80 rounded-full bg-gradient-to-br from-amber-300/20 to-transparent blur-3xl"/>
        <div className="absolute -bottom-32 -start-32 w-96 h-96 rounded-full bg-gradient-to-tr from-violet-400/15 to-transparent blur-3xl"/>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24">
          <button
            onClick={openBookingWithCart}
            data-testid="hero-cta"
            className="inline-flex items-center gap-2 px-9 py-4 rounded-full text-stone-900 hover:scale-105 transition font-semibold shadow-2xl text-base sm:text-lg"
            style={{ background: "linear-gradient(135deg, #F5D795, #E5B770)" }}
          >
            {isRTL ? cms.hero_cta_ar : cms.hero_cta_en}
            <ArrowRight size={20} className={isRTL ? "rotate-180" : ""} />
          </button>
        </div>
      </section>

      {/* ═══════════════════ STATS BAND ═══════════════════ */}
      {cms.show_stats !== false && (
        <section className="relative -mt-8 sm:-mt-12 z-10 px-4 sm:px-6 max-w-6xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl border border-stone-100 grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-stone-100 overflow-hidden">
            {["clients", "years", "services", "rating"].map((k, i) => {
              const val = cms[`stats_${k}_value`];
              const lbl = isRTL ? cms[`stats_${k}_label_ar`] : cms[`stats_${k}_label_en`];
              if (!val) return null;
              const icons = { clients: Users, years: Award, services: Sparkles, rating: Star };
              const Ico = icons[k];
              return (
                <div key={k} className="p-5 sm:p-6 text-center" data-testid={`stat-${k}`}>
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl mb-2" style={{ background: `${primary}15`, color: primary }}>
                    <Ico size={18}/>
                  </div>
                  <div className="text-3xl sm:text-4xl font-bold" style={{ color: primary }}>{val}</div>
                  <div className="text-xs sm:text-sm text-stone-500 mt-1 tracking-wide">{lbl}</div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ═══════════════════ FEATURED ITEMS ═══════════════════
          Surfaces whatever Services / Packages / Products the operator marked
          `is_featured = true`. Falls back gracefully when nothing is flagged. */}
      {cms.show_featured_items !== false && featuredItems.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 scroll-mt-20">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs tracking-widest uppercase mb-3" style={{ background: `${primary}10`, color: primary }}>
              <Crown size={12}/>{isRTL ? "اختيارنا" : "Our Picks"}
            </div>
            <h3 className="text-3xl sm:text-4xl font-bold" style={{ color: primary }}>
              {isRTL ? "خدماتنا المميزة" : "Featured Services"}
            </h3>
            <p className="text-stone-500 mt-2 max-w-xl mx-auto">
              {isRTL ? "أفضل ما نقدمه — مختارة بعناية لتجربة استثنائية" : "Our finest — handpicked for a remarkable experience"}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featuredItems.slice(0, 6).map((it) => {
              const id = it.service_id || it.package_id || it.product_id;
              const kind = it.service_id ? "service" : it.package_id ? "package" : "product";
              const KindIcon = kind === "service" ? Scissors : kind === "package" ? Gift : PackageIcon;
              const img = it.image_url ? resolveImg(it.image_url) : "";
              return (
                <div key={`feat-${id}`} className="group relative overflow-hidden rounded-2xl bg-white shadow-md hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 border border-stone-100">
                  <div className="aspect-[4/3] relative overflow-hidden">
                    {img ? (
                      <img src={img} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"/>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${primary}, ${accent})` }}>
                        <KindIcon size={56} className="text-white/40"/>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent"/>
                    <div className="absolute top-3 start-3 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur-sm text-xs font-bold flex items-center gap-1" style={{ color: primary }}>
                      <Sparkles size={11}/>{isRTL ? "مميز" : "Featured"}
                    </div>
                    <div className="absolute bottom-3 end-3 px-3 py-1.5 rounded-xl bg-white/95 backdrop-blur-sm font-bold" style={{ color: accent }}>
                      {it.sale_price != null && it.sale_price !== "" ? it.sale_price : it.price} {isRTL ? "ر.ق" : "QAR"}
                    </div>
                  </div>
                  <div className="p-5">
                    <h4 className="text-lg font-bold text-stone-800 line-clamp-1">{isRTL ? it.name_ar : it.name_en}</h4>
                    {(it.description_en || it.description_ar) && (
                      <p className="text-sm text-stone-500 mt-1.5 line-clamp-2 min-h-[2.5rem]">{isRTL ? it.description_ar : it.description_en}</p>
                    )}
                    {kind !== "product" ? (
                      <button
                        onClick={() => addToCart(kind, it)}
                        data-testid={`feat-add-${id}`}
                        className="mt-4 w-full text-white py-2.5 rounded-lg text-sm font-semibold hover:opacity-90 flex items-center justify-center gap-1.5 transition"
                        style={{ background: primary }}
                      >
                        <Plus size={14}/>{isRTL ? "أضف للسلة" : "Add to cart"}
                      </button>
                    ) : (
                      <div className="mt-4 text-center text-xs text-stone-400">
                        {(it.stock || 0) > 0 ? (isRTL ? "متوفر — اطلب عبر الفرع" : "Available — order at branch") : (isRTL ? "نفذ المخزون" : "Out of stock")}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ═══════════════════ TABBED CATALOG ═══════════════════
          Single tabbed band that replaces the older long-scrolling
          services → packages → products sections. The cashier-facing booking
          flow remains identical regardless of which tab the customer is on. */}
      <section id="catalog" className="bg-white py-16 scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h3 className="text-3xl sm:text-4xl font-bold" style={{ color: primary }}>
              {isRTL ? "كل ما نقدمه" : "Everything We Offer"}
            </h3>
            <p className="text-stone-500 mt-2">{isRTL ? "اختر القسم الذي يناسبك" : "Pick the section you want"}</p>
          </div>

          {/* Catalog search box — searches services + packages + products by
              name/description, hides the tab strip while there's a query and
              shows a unified cross-category result grid the customer can add
              to cart in one tap. */}
          <div className="max-w-2xl mx-auto mb-6">
            <div className="bg-white rounded-2xl shadow-md border border-stone-200 flex items-center gap-3 px-4 py-2.5">
              <Sparkles size={18} style={{ color: primary }} className="shrink-0" />
              <input
                value={catalogQ}
                onChange={(e) => setCatalogQ(e.target.value)}
                placeholder={isRTL ? "ابحث عن خدمة، باقة، أو منتج..." : "Search a service, package, or product..."}
                data-testid="public-catalog-search"
                className="flex-1 bg-transparent outline-none text-sm placeholder:text-stone-400"
              />
              {catalogQ && (
                <button
                  onClick={() => setCatalogQ("")}
                  data-testid="public-catalog-search-clear"
                  className="opacity-50 hover:opacity-100 transition"
                >
                  <X size={15}/>
                </button>
              )}
            </div>
            {searchActive && (
              <div className="text-xs text-stone-500 mt-2 text-center" data-testid="public-search-result-count">
                {totalMatches === 0
                  ? (isRTL ? "لا توجد نتائج مطابقة" : "No matching results")
                  : (isRTL
                      ? `${totalMatches} نتيجة — ${matchedServices.length} خدمة · ${matchedPackages.length} باقة · ${matchedProducts.length} منتج`
                      : `${totalMatches} results — ${matchedServices.length} services · ${matchedPackages.length} packages · ${matchedProducts.length} products`)
                }
              </div>
            )}
          </div>

          {/* Tab pills (hidden while searching — search aggregates across all three) */}
          {!searchActive && (
          <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
            {[
              { id: "services", icon: Scissors, count: services.length, label: isRTL ? "الخدمات" : "Services" },
              { id: "packages", icon: Gift, count: packages.length, label: isRTL ? "الباقات" : "Packages" },
              { id: "products", icon: PackageIcon, count: products.length, label: isRTL ? "المنتجات" : "Products" },
            ].map(({ id, icon: Ico, count, label }) => {
              const isActive = catalogTab === id;
              return (
                <button
                  key={id}
                  onClick={() => setCatalogTab(id)}
                  data-testid={`catalog-tab-${id}`}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border transition-all ${
                    isActive
                      ? "text-white border-transparent shadow-lg -translate-y-0.5"
                      : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"
                  }`}
                  style={isActive ? { background: `linear-gradient(135deg, ${primary}, ${accent})` } : {}}
                >
                  <Ico size={15}/>
                  {label}
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive ? "bg-white/25" : "bg-stone-100"}`}>{count}</span>
                </button>
              );
            })}
          </div>
          )}

          {/* When searching: show unified cross-category results */}
          {searchActive && totalMatches === 0 && (
            <div className="text-center text-stone-400 py-16 text-sm">
              {isRTL ? "لم نجد ما يطابق بحثك — جرّب كلمة أخرى" : "No matches — try a different keyword"}
            </div>
          )}

          {/* Services panel (rendered when searching OR active tab=services) */}
          {(searchActive ? matchedServices.length > 0 : catalogTab === "services") && (
            <div className="mb-10" data-testid="catalog-section-services">
              {searchActive && (
                <h4 className="text-sm font-bold uppercase tracking-wider mb-3 flex items-center gap-2" style={{ color: primary }}>
                  <Scissors size={14}/>{isRTL ? "الخدمات" : "Services"} <span className="text-stone-400 font-normal">({matchedServices.length})</span>
                </h4>
              )}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {visibleServices.length === 0 && !searchActive && (
                  <div className="col-span-full text-center text-stone-400 py-12">{isRTL ? "لا توجد خدمات بعد" : "No services yet"}</div>
                )}
                {visibleServices.map((s) => (
                  <div key={s.service_id} className="group bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-stone-100 overflow-hidden" data-testid={`public-service-${s.service_id}`}>
                    <div className="aspect-[4/3] relative overflow-hidden bg-stone-100">
                      {s.image_url ? (
                        <img src={resolveImg(s.image_url)} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"/>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${primary}25, ${accent}25)` }}>
                          <Scissors size={42} style={{ color: primary, opacity: 0.5 }}/>
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h4 className="text-base font-semibold text-stone-800 line-clamp-1">{isRTL ? s.name_ar : s.name_en}</h4>
                      {(s.description_en || s.description_ar) && (
                        <p className="text-xs text-stone-500 mt-1 line-clamp-2 min-h-[2rem]">{isRTL ? s.description_ar : s.description_en}</p>
                      )}
                      <div className="flex items-end justify-between mt-3">
                        <div>
                          <div className="text-xl font-bold" style={{ color: accent }}>{s.price} <span className="text-xs font-normal">{isRTL ? "ر.ق" : "QAR"}</span></div>
                          <div className="text-[11px] text-stone-400 flex items-center gap-1 mt-0.5"><Clock size={10}/>{s.duration_min} {isRTL ? "د" : "min"}</div>
                        </div>
                        <button
                          onClick={() => addToCart("service", s)}
                          data-testid={`add-service-${s.service_id}`}
                          className="text-white px-3 py-2 rounded-lg text-xs font-semibold hover:scale-105 flex items-center gap-1 transition"
                          style={{ background: primary }}
                        >
                          <Plus size={12}/>{isRTL ? "أضف" : "Add"}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Browse all CTA — visible only outside search mode and when there's more than the preview limit */}
              {!searchActive && matchedServices.length > PREVIEW_LIMIT && !showAllByTab.services && (
                <div className="text-center mt-6">
                  <button
                    onClick={() => setShowAllByTab((x) => ({ ...x, services: true }))}
                    data-testid="browse-all-services"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold border-2 transition hover:bg-stone-50"
                    style={{ borderColor: primary, color: primary }}
                  >
                    {isRTL ? `تصفّح كل الخدمات (${matchedServices.length})` : `Browse all services (${matchedServices.length})`}
                    <ArrowRight size={14} className={isRTL ? "rotate-180" : ""}/>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Packages panel */}
          {(searchActive ? matchedPackages.length > 0 : catalogTab === "packages") && (
            <div className="mb-10" data-testid="catalog-section-packages">
              {searchActive && (
                <h4 className="text-sm font-bold uppercase tracking-wider mb-3 flex items-center gap-2" style={{ color: primary }}>
                  <Gift size={14}/>{isRTL ? "الباقات" : "Packages"} <span className="text-stone-400 font-normal">({matchedPackages.length})</span>
                </h4>
              )}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {visiblePackages.length === 0 && !searchActive && (
                  <div className="col-span-full text-center text-stone-400 py-12">{isRTL ? "لا توجد باقات" : "No packages"}</div>
                )}
                {visiblePackages.map((p) => (
                  <div key={p.package_id} className="group relative rounded-2xl overflow-hidden text-white shadow-md hover:shadow-2xl hover:-translate-y-1 transition-all duration-500" style={{ background: `linear-gradient(135deg, ${primary}, ${accent})` }} data-testid={`public-package-${p.package_id}`}>
                    {p.image_url && (
                      <div className="absolute inset-0 opacity-30 mix-blend-overlay">
                        <img src={resolveImg(p.image_url)} alt="" className="w-full h-full object-cover"/>
                      </div>
                    )}
                    <div className="relative p-6">
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur-sm text-[10px] tracking-widest uppercase mb-3">
                        <Gift size={11}/>{isRTL ? "باقة مميزة" : "Premium Package"}
                      </div>
                      <h4 className="text-xl font-bold mt-1">{isRTL ? p.name_ar : p.name_en}</h4>
                      {(p.description_en || p.description_ar) && (
                        <p className="text-sm opacity-90 mt-1.5 line-clamp-2 min-h-[2.5rem]">{isRTL ? p.description_ar : p.description_en}</p>
                      )}
                      <div className="text-3xl font-bold mt-4 drop-shadow">{p.price} <span className="text-sm font-normal opacity-90">{isRTL ? "ر.ق" : "QAR"}</span></div>
                      <button
                        onClick={() => addToCart("package", p)}
                        data-testid={`add-package-${p.package_id}`}
                        className="mt-4 px-4 py-2 rounded-lg text-sm font-semibold bg-white text-stone-900 hover:scale-105 transition inline-flex items-center gap-1.5"
                      >
                        <Plus size={14}/>{isRTL ? "أضف للسلة" : "Add to cart"}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              {!searchActive && matchedPackages.length > PREVIEW_LIMIT && !showAllByTab.packages && (
                <div className="text-center mt-6">
                  <button
                    onClick={() => setShowAllByTab((x) => ({ ...x, packages: true }))}
                    data-testid="browse-all-packages"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold border-2 transition hover:bg-stone-50"
                    style={{ borderColor: primary, color: primary }}
                  >
                    {isRTL ? `تصفّح كل الباقات (${matchedPackages.length})` : `Browse all packages (${matchedPackages.length})`}
                    <ArrowRight size={14} className={isRTL ? "rotate-180" : ""}/>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Products panel */}
          {(searchActive ? matchedProducts.length > 0 : catalogTab === "products") && (
            <div className="mb-10" data-testid="catalog-section-products">
              {searchActive && (
                <h4 className="text-sm font-bold uppercase tracking-wider mb-3 flex items-center gap-2" style={{ color: primary }}>
                  <PackageIcon size={14}/>{isRTL ? "المنتجات" : "Products"} <span className="text-stone-400 font-normal">({matchedProducts.length})</span>
                </h4>
              )}
              <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
                {visibleProducts.length === 0 && !searchActive && (
                  <div className="col-span-full text-center text-stone-400 py-12">{isRTL ? "لا توجد منتجات" : "No products"}</div>
                )}
                {visibleProducts.map((p) => {
                  const onSale = p.sale_price != null && p.sale_price !== "";
                  return (
                    <div key={p.product_id} className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-stone-100 overflow-hidden" data-testid={`public-product-${p.product_id}`}>
                      <div className="aspect-square bg-stone-100 overflow-hidden relative">
                        {p.image_url ? (
                          <img src={resolveImg(p.image_url)} alt="" className="w-full h-full object-cover hover:scale-105 transition duration-500"/>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${primary}25, ${accent}25)` }}>
                            <PackageIcon size={42} style={{ color: primary, opacity: 0.5 }}/>
                          </div>
                        )}
                        {onSale && (
                          <span className="absolute top-2 start-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-rose-500 text-white shadow">
                            {isRTL ? "عرض" : "Sale"}
                          </span>
                        )}
                      </div>
                      <div className="p-4">
                        <h4 className="text-sm font-semibold text-stone-800 line-clamp-2 min-h-[2.5rem]">{isRTL ? p.name_ar : p.name_en}</h4>
                        <div className="flex items-baseline gap-2 mt-3">
                          {onSale ? (
                            <>
                              <span className="text-lg font-bold" style={{ color: accent }}>{p.sale_price}</span>
                            <span className="text-xs text-stone-400 line-through">{p.price}</span>
                          </>
                        ) : (
                          <span className="text-lg font-bold" style={{ color: accent }}>{p.price}</span>
                        )}
                        <span className="text-[10px] text-stone-400">{isRTL ? "ر.ق" : "QAR"}</span>
                      </div>
                      {(p.stock || 0) <= 0 ? (
                        <div className="mt-2 text-[11px] text-rose-500 font-medium">{isRTL ? "نفذ المخزون" : "Out of stock"}</div>
                      ) : (
                        <div className="mt-2 text-[11px] text-emerald-600">{isRTL ? "متوفر" : "In stock"}</div>
                      )}
                    </div>
                  </div>
                );
                })}
              </div>
              {!searchActive && matchedProducts.length > PREVIEW_LIMIT && !showAllByTab.products && (
                <div className="text-center mt-6">
                  <button
                    onClick={() => setShowAllByTab((x) => ({ ...x, products: true }))}
                    data-testid="browse-all-products"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold border-2 transition hover:bg-stone-50"
                    style={{ borderColor: primary, color: primary }}
                  >
                    {isRTL ? `تصفّح كل المنتجات (${matchedProducts.length})` : `Browse all products (${matchedProducts.length})`}
                    <ArrowRight size={14} className={isRTL ? "rotate-180" : ""}/>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ═══════════════════ WHY CHOOSE US ═══════════════════ */}
      {cms.show_features !== false && (cms.features || []).length > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
          <div className="text-center mb-10">
            <h3 className="text-3xl sm:text-4xl font-bold" style={{ color: primary }}>
              {isRTL ? "لماذا نحن؟" : "Why Choose Us"}
            </h3>
            <p className="text-stone-500 mt-2">{isRTL ? "ما يميزنا عن غيرنا" : "What sets us apart"}</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {(cms.features || []).map((f, i) => {
              const Ico = FEATURE_ICON_MAP[f.icon] || Sparkles;
              return (
                <div key={i} className="bg-white rounded-2xl p-6 border border-stone-100 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 text-center" data-testid={`feature-card-${i}`}>
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mx-auto mb-4" style={{ background: `linear-gradient(135deg, ${primary}20, ${accent}20)`, color: primary }}>
                    <Ico size={26}/>
                  </div>
                  <h4 className="text-base font-bold text-stone-800">{isRTL ? f.title_ar : f.title_en}</h4>
                  {(f.desc_ar || f.desc_en) && (
                    <p className="text-sm text-stone-500 mt-2 leading-relaxed">{isRTL ? f.desc_ar : f.desc_en}</p>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* About — section renders whenever show_about is enabled, even if the
          operator hasn't filled the about text yet. This is the fix for the
          dead "من نحن" nav link: previously the section was gated behind
          `about_text_en || about_text_ar` so clicking #about scrolled to
          nowhere when texts were empty. We now always render the heading and
          fall back to a friendly placeholder so the anchor is always real. */}
      {cms.show_about && (
        <section id="about" className="bg-white py-16 scroll-mt-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-10 items-center">
            <div>
              <h3 className="text-3xl font-bold" style={{ color: primary }}>
                {isRTL ? (cms.about_title_ar || "من نحن") : (cms.about_title_en || "About Us")}
              </h3>
              {(isRTL ? cms.about_text_ar : cms.about_text_en) ? (
                <p className="mt-4 text-stone-600 leading-relaxed whitespace-pre-line">
                  {isRTL ? cms.about_text_ar : cms.about_text_en}
                </p>
              ) : (
                <p className="mt-4 text-stone-500 leading-relaxed italic">
                  {isRTL
                    ? `مرحبًا بك في ${cms.name_ar || "آي سالون"}. نحن نلتزم بتقديم أعلى مستويات الخدمة والجودة لعملائنا الكرام.`
                    : `Welcome to ${cms.name_en || "iSaloon"}. We are committed to delivering the highest standards of service and quality to our valued clients.`}
                </p>
              )}
            </div>
            {cms.about_image_url && <img src={resolveImg(cms.about_image_url)} alt="" className="rounded-xl shadow-md w-full max-h-96 object-cover" />}
          </div>
        </section>
      )}

      {/* Gallery */}
      {cms.show_gallery && cms.gallery?.length > 0 && (
        <section id="gallery" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 scroll-mt-20">
          <h3 className="text-3xl font-bold text-center" style={{ color: primary }}>
            {isRTL ? "المعرض" : "Gallery"}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-8">
            {cms.gallery.map((g, i) => (
              <div key={g.image_url || `gallery-${i}`} className="relative group overflow-hidden rounded-lg aspect-square">
                <img src={resolveImg(g.image_url)} alt="" className="w-full h-full object-cover group-hover:scale-110 transition duration-500" />
                {(g.caption_en || g.caption_ar) && (
                  <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/80 to-transparent text-white text-sm">
                    {isRTL ? g.caption_ar : g.caption_en}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Testimonials */}
      {cms.show_testimonials && (cms.testimonials?.length > 0 || reviews?.length > 0) && (
        <section className="bg-white py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <h3 className="text-3xl font-bold text-center" style={{ color: primary }}>
              {isRTL ? "آراء عملائنا" : "Testimonials"}
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
              {[...(cms.testimonials || []), ...reviews.map((r) => ({ customer_name: r.customer_name, rating: r.rating, text_en: r.comment, text_ar: r.comment }))].slice(0, 9).map((t, i) => (
                <div key={`${t.customer_name || "anon"}:${i}`} className="bg-stone-50 rounded-xl p-5 border border-stone-100">
                  <div className="flex items-center gap-1 mb-2">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} size={16} fill={n <= (t.rating || 5) ? accent : "none"} color={accent} />
                    ))}
                  </div>
                  <p className="text-stone-600 italic">&ldquo;{isRTL ? t.text_ar : t.text_en}&rdquo;</p>
                  <div className="mt-3 font-semibold text-stone-800">— {t.customer_name}</div>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <button
                onClick={() => setShowReviewModal(true)}
                data-testid="public-review-btn"
                className="px-5 py-2 rounded-md border-2 hover:bg-stone-50"
                style={{ borderColor: primary, color: primary }}
              >
                {isRTL ? "أضف تقييمك" : "Leave a Review"}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Contact */}
      {cms.show_contact && (
        <section id="contact" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 scroll-mt-20">
          <h3 className="text-3xl font-bold text-center" style={{ color: primary }}>
            {isRTL ? "تواصل معنا" : "Contact Us"}
          </h3>
          <div className="grid md:grid-cols-2 gap-6 mt-8">
            <div className="bg-white rounded-xl p-6 border border-stone-100 space-y-3">
              {cms.address_en && <div className="flex gap-3"><MapPin size={20} style={{ color: primary }} /><span className="text-stone-600">{isRTL ? cms.address_ar : cms.address_en}</span></div>}
              {cms.phone && <div className="flex gap-3"><Phone size={20} style={{ color: primary }} /><a href={`tel:${cms.phone}`} className="text-stone-600 hover:text-[var(--p)]">{cms.phone}</a></div>}
              {cms.email && <div className="flex gap-3"><Mail size={20} style={{ color: primary }} /><a href={`mailto:${cms.email}`} className="text-stone-600 hover:text-[var(--p)]">{cms.email}</a></div>}
              {cms.whatsapp && <div className="flex gap-3"><MessageCircle size={20} style={{ color: primary }} /><a href={`https://wa.me/${cms.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="text-stone-600 hover:text-[var(--p)]">WhatsApp</a></div>}
              {cms.instagram && <div className="flex gap-3"><Instagram size={20} style={{ color: primary }} /><a href={`https://instagram.com/${cms.instagram.replace("@", "")}`} target="_blank" rel="noreferrer" className="text-stone-600 hover:text-[var(--p)]">{cms.instagram}</a></div>}
              <div className="flex gap-3"><Clock size={20} style={{ color: primary }} /><span className="text-stone-600">{isRTL ? cms.working_hours_ar : cms.working_hours_en}</span></div>
            </div>
            {cms.map_embed_url && <iframe src={cms.map_embed_url} className="w-full h-72 rounded-xl border border-stone-100" loading="lazy" />}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-stone-900 text-white py-8 text-center text-sm">
        <p>© {new Date().getFullYear()} {isRTL ? cms.name_ar : cms.name_en}. {isRTL ? "جميع الحقوق محفوظة" : "All rights reserved."}</p>
        <Link to="/login" className="text-stone-500 hover:text-white mt-1 inline-block text-xs">{isRTL ? "دخول الإدارة" : "Admin Login"}</Link>
      </footer>

      {/* Booking Modal with Cart */}
      {showBookingModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onMouseDown={(e) => onBackdropMouseDown(e, () => setShowBookingModal(false))}>
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-auto" onMouseDown={onModalPanelMouseDown}>
            <h3 className="text-2xl font-bold" style={{ color: primary }}>{isRTL ? "إكمال الحجز" : "Complete Booking"}</h3>
            <p className="text-sm text-stone-500 mt-1">{isRTL ? "راجع اختياراتك واملأ بياناتك" : "Review your selections and fill your details"}</p>

            {/* Cart Items */}
            <div className="mt-4 space-y-2 border border-stone-200 rounded-lg p-3 bg-stone-50">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-stone-700">{isRTL ? "السلة" : "Your Cart"} ({cartCount})</span>
                {cart.length > 0 && <button onClick={clearCart} className="text-xs text-rose-500 hover:underline flex items-center gap-1"><Trash2 size={12} /> {isRTL ? "إفراغ" : "Clear"}</button>}
              </div>
              {cart.length === 0 && <div className="text-center text-stone-400 py-4 text-sm">{isRTL ? "السلة فارغة" : "Cart is empty"}</div>}
              {cart.map((c, idx) => (
                <div key={`${c.kind}:${c.id || c.ref_id || idx}`} className="flex items-center gap-2 bg-white p-2 rounded-md border border-stone-100">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate text-stone-800">{isRTL ? c.name_ar : c.name_en}</div>
                    <div className="text-xs text-stone-500">{c.kind === "service" ? (isRTL ? "خدمة" : "Service") : (isRTL ? "باقة" : "Package")} · {c.price} {isRTL ? "ر.ق" : "QAR"}</div>
                  </div>
                  <div className="flex items-center gap-1 bg-stone-100 rounded-md text-stone-800">
                    <button onClick={() => updateQty(idx, -1)} className="p-1 hover:bg-stone-200 rounded text-stone-700" data-testid={`cart-decrement-${idx}`}><Minus size={12} /></button>
                    <span className="px-2 text-sm font-semibold text-stone-900 min-w-[1.25rem] text-center">{c.qty}</span>
                    <button onClick={() => updateQty(idx, 1)} className="p-1 hover:bg-stone-200 rounded text-stone-700" data-testid={`cart-increment-${idx}`}><Plus size={12} /></button>
                  </div>
                  <div className="text-sm font-semibold w-16 text-end" style={{ color: accent }}>{(c.price * c.qty).toFixed(0)}</div>
                  <button onClick={() => removeFromCart(idx)} className="p-1 text-rose-400 hover:text-rose-600" data-testid={`cart-remove-${idx}`}><X size={14} /></button>
                </div>
              ))}
              {cart.length > 0 && (
                <div className="flex items-center justify-between pt-2 mt-2 border-t border-stone-200 font-semibold text-stone-800">
                  <span>{isRTL ? "الإجمالي" : "Total"}</span>
                  <span style={{ color: accent }}>{cartTotal.toFixed(2)} {isRTL ? "ر.ق" : "QAR"}</span>
                </div>
              )}
            </div>

            {/* Customer Info */}
            <div className="space-y-3 mt-4">
              <input data-testid="booking-name" placeholder={isRTL ? "الاسم *" : "Name *"} className="w-full px-3 py-2.5 border-2 border-stone-300 rounded-md bg-white text-black font-medium placeholder:text-stone-500 focus:outline-none focus:border-stone-700" style={{ color: "#000" }} value={bookingForm.customer_name} onChange={(e) => setBookingForm({ ...bookingForm, customer_name: e.target.value })} />
              <input data-testid="booking-phone" placeholder={isRTL ? "رقم الجوال * (+974 5XXX XXXX)" : "Phone * (+974 5XXX XXXX)"} dir="ltr" className="w-full px-3 py-2.5 border-2 border-stone-300 rounded-md bg-white text-black font-medium placeholder:text-stone-500 focus:outline-none focus:border-stone-700" style={{ color: "#000" }} value={bookingForm.customer_phone} onChange={(e) => setBookingForm({ ...bookingForm, customer_phone: e.target.value })} />
              <input data-testid="booking-email" placeholder={isRTL ? "البريد (اختياري)" : "Email (optional)"} className="w-full px-3 py-2.5 border-2 border-stone-300 rounded-md bg-white text-black font-medium placeholder:text-stone-500 focus:outline-none focus:border-stone-700" style={{ color: "#000" }} value={bookingForm.customer_email} onChange={(e) => setBookingForm({ ...bookingForm, customer_email: e.target.value })} />
              <div className="grid grid-cols-2 gap-3">
                <input type="date" data-testid="booking-date" min={new Date().toISOString().split("T")[0]} className="px-3 py-2.5 border-2 border-stone-300 rounded-md bg-white text-black font-medium focus:outline-none focus:border-stone-700" style={{ color: "#000" }} value={bookingForm.date} onChange={(e) => setBookingForm({ ...bookingForm, date: e.target.value, time: "" })} />
                <div className="px-3 py-2 border rounded-md flex items-center text-sm text-stone-500 bg-stone-50">
                  {bookingForm.time ? (
                    <span className="font-semibold" style={{ color: primary }}>⏰ {bookingForm.time}</span>
                  ) : (
                    <span>{isRTL ? "اختر الوقت أدناه" : "Select time below"}</span>
                  )}
                </div>
              </div>

              {/* Slot Picker */}
              {bookingForm.date && (
                <div className="space-y-2">
                  <div className="text-sm font-semibold text-stone-700 flex items-center justify-between">
                    <span>{isRTL ? "اختر وقت الموعد" : "Pick a time slot"}</span>
                    {slotsLoading && <span className="text-xs text-stone-400">{isRTL ? "جارٍ التحميل..." : "Loading..."}</span>}
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {slots.map((s) => {
                      const isFull = s.remaining <= 0;
                      const isSelected = bookingForm.time === s.time_slot;
                      const isLow = !isFull && s.remaining <= Math.max(1, Math.ceil(s.capacity * 0.3));
                      return (
                        <button
                          key={s.time_slot}
                          type="button"
                          disabled={isFull}
                          data-testid={`slot-${s.time_slot}`}
                          onClick={() => setBookingForm({ ...bookingForm, time: s.time_slot })}
                          className={`p-2 rounded-md text-xs border transition relative ${
                            isSelected
                              ? "text-white border-transparent shadow-md"
                              : isFull
                              ? "bg-rose-50 border-rose-200 text-rose-400 cursor-not-allowed line-through"
                              : isLow
                              ? "bg-amber-50 border-amber-200 text-amber-700 hover:border-amber-400"
                              : "bg-white border-stone-200 text-stone-700 hover:border-stone-400"
                          }`}
                          style={isSelected ? { background: primary } : {}}
                        >
                          <div className="font-bold text-sm">{s.time_slot}</div>
                          <div className="text-[10px] opacity-80 mt-0.5">
                            {isFull
                              ? (isRTL ? "مكتمل" : "Full")
                              : isRTL
                              ? `متاح ${s.remaining}/${s.capacity}`
                              : `${s.remaining}/${s.capacity} left`}
                          </div>
                        </button>
                      );
                    })}
                    {slots.length === 0 && !slotsLoading && (
                      <div className="col-span-full text-center text-sm text-stone-400 py-3">
                        {isRTL ? "لا توجد فترات متاحة" : "No slots available"}
                      </div>
                    )}
                  </div>
                  {slots.length > 0 && (
                    <div className="flex flex-wrap items-center gap-3 text-[10px] text-stone-500 pt-1">
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-white border border-stone-300"></span>{isRTL ? "متاح" : "Available"}</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-50 border border-amber-300"></span>{isRTL ? "قارب الامتلاء" : "Almost full"}</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-rose-50 border border-rose-200"></span>{isRTL ? "مكتمل" : "Full"}</span>
                    </div>
                  )}
                </div>
              )}
              <input data-testid="booking-coupon" placeholder={isRTL ? "كود الخصم (اختياري)" : "Coupon code (optional)"} className="w-full px-3 py-2.5 border-2 border-stone-300 rounded-md bg-white text-black font-medium placeholder:text-stone-500 focus:outline-none focus:border-stone-700" style={{ color: "#000" }} value={bookingForm.coupon_code} onChange={(e) => setBookingForm({ ...bookingForm, coupon_code: e.target.value })} />
              <textarea data-testid="booking-notes" placeholder={isRTL ? "ملاحظات (اختياري)" : "Notes (optional)"} className="w-full px-3 py-2.5 border-2 border-stone-300 rounded-md bg-white text-black font-medium placeholder:text-stone-500 focus:outline-none focus:border-stone-700" style={{ color: "#000" }} rows={2} value={bookingForm.notes} onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })} />
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowBookingModal(false)} className="flex-1 px-4 py-2 border border-stone-300 rounded-md text-stone-700 hover:bg-stone-50">{isRTL ? "إلغاء" : "Cancel"}</button>
              <button data-testid="booking-submit" onClick={submitBooking} disabled={cart.length === 0} className="flex-1 px-4 py-2 text-white rounded-md hover:opacity-90 flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: primary }}>
                <Send size={16} /> {isRTL ? `تأكيد الحجز (${cartTotal.toFixed(0)} ر.ق)` : `Confirm (${cartTotal.toFixed(0)} QAR)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onMouseDown={(e) => onBackdropMouseDown(e, () => setShowReviewModal(false))}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full" onMouseDown={onModalPanelMouseDown}>
            <h3 className="text-2xl font-bold" style={{ color: primary }}>{isRTL ? "أضف تقييمك" : "Leave a Review"}</h3>
            <div className="space-y-3 mt-4">
              <input data-testid="review-name" placeholder={isRTL ? "الاسم" : "Name"} className="w-full px-3 py-2 border border-stone-300 rounded-md bg-white text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-stone-500" value={reviewForm.customer_name} onChange={(e) => setReviewForm({ ...reviewForm, customer_name: e.target.value })} />
              <input data-testid="review-phone" placeholder={isRTL ? "رقم الجوال (اختياري)" : "Phone (optional)"} className="w-full px-3 py-2 border border-stone-300 rounded-md bg-white text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-stone-500" value={reviewForm.customer_phone} onChange={(e) => setReviewForm({ ...reviewForm, customer_phone: e.target.value })} />
              <div className="flex items-center gap-2">
                <span className="text-sm text-stone-700">{isRTL ? "التقييم:" : "Rating:"}</span>
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => setReviewForm({ ...reviewForm, rating: n })}>
                    <Star size={24} fill={n <= reviewForm.rating ? accent : "none"} color={accent} />
                  </button>
                ))}
              </div>
              <textarea data-testid="review-comment" placeholder={isRTL ? "تعليقك" : "Your comment"} rows={4} className="w-full px-3 py-2 border border-stone-300 rounded-md bg-white text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-stone-500" value={reviewForm.comment} onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })} />
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowReviewModal(false)} className="flex-1 px-4 py-2 border border-stone-300 rounded-md text-stone-700 hover:bg-stone-50">{isRTL ? "إلغاء" : "Cancel"}</button>
              <button data-testid="review-submit" onClick={submitReview} className="flex-1 px-4 py-2 text-white rounded-md" style={{ background: primary }}>{isRTL ? "إرسال" : "Submit"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
